import {useMemo} from "react";

import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {CheckTypeDisplay} from "@/lib/types/checks.ts";
import {ANALYZER_PLACEHOLDER_CHECKS, ANALYZER_PLACEHOLDER_FIELDS} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerChecksTableProps, AnalyzerPhase, ProposedCheck} from "@/pages/analyzer/Analyzer.types.ts";
import {analyzerCheckColumn, getProposedCheckRowId, getUncreatedMemberIds} from "@/pages/analyzer/Analyzer.utils.ts";
import {AnalyzerCheckContactsCell} from "@/pages/analyzer/components/AnalyzerCheckContactsCell.tsx";
import {AnalyzerCheckStatusCell} from "@/pages/analyzer/components/AnalyzerCheckStatusCell.tsx";
import {AnalyzerCheckTagsCell} from "@/pages/analyzer/components/AnalyzerCheckTagsCell.tsx";
import {AnalyzerQuickActions} from "@/pages/analyzer/components/AnalyzerQuickActions.tsx";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

const TABLE_SPECS: ColumnSpec<ProposedCheck>[] = [
  {
    ...analyzerCheckColumn("monitoring_service_type"),
    accessor: ({monitoring_service_type}) => CheckTypeDisplay[monitoring_service_type],
  },
  analyzerCheckColumn("msp_address"),
  analyzerCheckColumn("name"),
  analyzerCheckColumn("justification"),
  {...analyzerCheckColumn("msp_interval"), accessor: ({msp_interval}) => msp_interval || undefined},
  analyzerCheckColumn("num_locations"),
  analyzerCheckColumn("msp_sensitivity"),
  analyzerCheckColumn("msp_expect_string"),
  analyzerCheckColumn("msp_port"),
  analyzerCheckColumn("msp_dns_record_type"),
  {...analyzerCheckColumn("tags"), accessor: (row) => <AnalyzerCheckTagsCell check={row} />},
  {...analyzerCheckColumn("contact_groups"), accessor: (row) => <AnalyzerCheckContactsCell check={row} />},
];

const PLACEHOLDER_SPECS: ColumnSpec<ProposedCheck>[] = ANALYZER_PLACEHOLDER_FIELDS.map((fieldName) => ({
  ...analyzerCheckColumn(fieldName),
  accessor: (row) => (
    <span>
      {fieldName === "monitoring_service_type" ? CheckTypeDisplay[row.monitoring_service_type] : row[fieldName]}
    </span>
  ),
}));

export const AnalyzerChecksTable = ({checks, hideEmptyOptionalColumns}: AnalyzerChecksTableProps) => {
  const {selectedChecks, setSelectedChecks, phase, proposedChecks, positionById, awaitingProposals, creationOutcome} =
    useAnalyzerContext();
  const createdByProposalId = creationOutcome?.createdByProposalId ?? {};
  const canGrow = (check: ProposedCheck) => getUncreatedMemberIds(check, createdByProposalId).length > 0;

  const getEmptyStateTitle = () => {
    if (proposedChecks.length) {
      return "No Checks match your search.";
    }
    if (phase === AnalyzerPhase.ABORTED) {
      return "Analysis stopped before Checks were proposed. Retry from the chat to continue.";
    }
    if (phase === AnalyzerPhase.ERROR) {
      return "Analysis failed before Checks were proposed. Retry from the chat to continue.";
    }
    return "No Checks proposed. Try sharing more details in the chat.";
  };
  const emptyStateTitle = getEmptyStateTitle();

  const specs = useMemo(() => {
    if (!checks.length) {
      return PLACEHOLDER_SPECS;
    }
    const positionSpec: ColumnSpec<ProposedCheck> = {
      fieldName: "position",
      fieldTitle: "#",
      accessor: (row) => positionById.get(row.id),
    };
    const visible = [
      positionSpec,
      ...(hideEmptyOptionalColumns
        ? TABLE_SPECS.filter((spec) => {
            const isOptional = spec.fieldName.startsWith("msp_") || spec.fieldName === "num_locations";
            return !isOptional || checks.some((check) => check[spec.fieldName as keyof ProposedCheck]);
          })
        : TABLE_SPECS),
    ];
    if (!creationOutcome) {
      return visible;
    }
    const statusSpec: ColumnSpec<ProposedCheck> = {
      fieldName: "creation_status",
      fieldTitle: "Status",
      accessor: (row) => <AnalyzerCheckStatusCell check={row} />,
    };
    return [statusSpec, ...visible];
  }, [hideEmptyOptionalColumns, checks, creationOutcome, positionById]);

  return (
    <Table<ProposedCheck>
      rows={awaitingProposals ? ANALYZER_PLACEHOLDER_CHECKS : checks}
      specs={specs}
      getRowId={getProposedCheckRowId}
      rowSelection={selectedChecks}
      setRowSelection={setSelectedChecks}
      enableRowSelection={(row) =>
        !awaitingProposals &&
        phase !== AnalyzerPhase.STREAMING &&
        (!createdByProposalId[row.original.id] || canGrow(row.original))
      }
      actionBar={<AnalyzerQuickActions />}
      variant="square"
      fillContainerHeight
      placeholder={awaitingProposals}
      noDataComponent={<TableEmptyStateCta colSpan={specs.length + 1} title={emptyStateTitle} />}
    />
  );
};
